import React, { useMemo, useState } from "react";
import { SectionTitle, InputCss } from "../../_parts.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import CreditHistoryField from "../shared/CreditHistoryField.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { useBtlUploads } from "../shared/useBtlUploads.js";
import { buildUploadLabelMap, isValidEmail } from "../shared/formValidation.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import { arePhonesValid, isValidPhone, PHONE_ERROR_MOBILE } from "../../../../utils/validatePhone.js";
import { IF_FORM_WIZARD_STEPS, IF_UPLOAD_BUCKETS } from "../shared/borrowerUploadConfig.js";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const MAX_MEMBERS = 4;

export default function IfPensionForm({
  onBack,
  onNext,
  initialPayload,
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("if-pension", productId || initialPayload?.productId));
  const initial = savedDraft?.if?.pension || initialPayload?.if?.pension || {};

  const [scheme, setScheme] = useState({
    name: initial.scheme?.name || "",
    number: initial.scheme?.number || "",
  });
  const [credit, setCredit] = useState(initial.credit || "");
  const [members, setMembers] = useState(
    initial.members?.length
      ? initial.members
      : [{ id: uid(), firstName: "", surname: "", phone: "", email: "" }]
  );
  const { uploads, uploadingFiles, handleUpload, handleRemove } = useBtlUploads(initial.uploads || {});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const primary = members[0] || {};
  const touch = (key) => setTouched((t) => ({ ...t, [key]: true }));
  const updateMember = (id, key, val) => setMembers((arr) => arr.map((m) => (m.id === id ? { ...m, [key]: val } : m)));

  const payloadObj = useMemo(
    () => ({
      ...(initialPayload || {}),
      if: {
        ...(initialPayload?.if || {}),
        borrowerType: "Pension/Trust",
        pension: { scheme, members, uploads, credit },
      },
      formData: {
        ...(initialPayload?.formData || {}),
        credit,
        credit_history: credit,
      },
    }),
    [initialPayload, scheme, members, uploads, credit]
  );

  const validateDetails = () => {
    const err = {};
    if (!scheme.name.trim()) err.scheme_name = "Pension/Trust Name is required.";
    if (!primary.firstName?.trim()) err.member_firstName = "Member 1 First Name is required.";
    if (!primary.surname?.trim()) err.member_surname = "Member 1 Surname is required.";
    if (!isValidPhone(primary.phone)) err.member_phone = PHONE_ERROR_MOBILE;
    if (!primary.email?.trim()) err.member_email = "Member 1 Email is required.";
    else if (!isValidEmail(primary.email)) err.member_email = "Enter a valid email address.";
    if (!credit) err.credit = "Please select credit history.";
    setFieldErrors(err);
    setTouched(Object.fromEntries(Object.keys(err).map((k) => [k, true])));
    return !Object.keys(err).length;
  };

  const stepErrors = useMemo(
    () => ({
      details: Object.keys(fieldErrors).length > 0,
      documents: false,
      review: false,
    }),
    [fieldErrors]
  );

  const validateStep = async (stepIndex, stepId) => {
    if (stepId === "details" || stepIndex === 0) return validateDetails();
    return true;
  };

  const finish = (extras = {}) => {
    onNext?.({
      type: "pension",
      scheme,
      members,
      credit,
      uploads,
      payloadObj,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(IF_UPLOAD_BUCKETS), []);

  return (
    <>
      <BorrowerSteppedForm
        title="INVOICE FINANCE — PENSION / TRUST"
        subtitle="Please complete all required fields and upload necessary documents"
        steps={IF_FORM_WIZARD_STEPS}
        draftKey={getDraftStorageKey("if-pension", productId || initialPayload?.productId)}
        draftSnapshot={payloadObj}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        isSubmitting={isSubmitting}
        detailsContinueDisabled={!arePhonesValid(members.map((m, i) => ({ value: m.phone, required: i === 0 })))}
        renderDetails={() => (
          <div className="space-y-5">
            <div>
              <SectionTitle>Scheme</SectionTitle>
              <FormFieldRow>
                <FormField id="if-scheme-name" label="Pension/Trust Name" required error={fieldErrors.scheme_name} touched={touched.scheme_name}>
                  <input className="input w-full" value={scheme.name} onChange={(e) => setScheme((s) => ({ ...s, name: e.target.value }))} onBlur={() => touch("scheme_name")} />
                </FormField>
                <FormField id="if-scheme-number" label="Pension/Trust Number">
                  <input className="input w-full" value={scheme.number} onChange={(e) => setScheme((s) => ({ ...s, number: e.target.value }))} />
                </FormField>
              </FormFieldRow>
            </div>
            <CreditHistoryField id="if-pension-credit" value={credit} error={fieldErrors.credit} touched={touched.credit} onChange={setCredit} />
            <div>
              <SectionTitle>Members</SectionTitle>
              {members.map((m, idx) => (
                <div key={m.id} className="rounded-xl border border-slate-200 p-3 mb-3">
                  <div className="mb-2 flex justify-between items-center">
                    <p className="text-sm font-bold text-slate-900">Member {idx + 1}</p>
                    {members.length > 1 ? (
                      <button type="button" className="text-xs text-red-500" onClick={() => setMembers((list) => list.filter((x) => x.id !== m.id))}>Remove</button>
                    ) : null}
                  </div>
                  <FormFieldRow>
                    <FormField id={`if-m-${m.id}-fn`} label="First Name" required={idx === 0} error={idx === 0 ? fieldErrors.member_firstName : undefined} touched={idx === 0 ? touched.member_firstName : false}>
                      <input className="input w-full" value={m.firstName} onChange={(e) => updateMember(m.id, "firstName", e.target.value)} onBlur={() => idx === 0 && touch("member_firstName")} />
                    </FormField>
                    <FormField id={`if-m-${m.id}-sn`} label="Surname" required={idx === 0} error={idx === 0 ? fieldErrors.member_surname : undefined} touched={idx === 0 ? touched.member_surname : false}>
                      <input className="input w-full" value={m.surname} onChange={(e) => updateMember(m.id, "surname", e.target.value)} onBlur={() => idx === 0 && touch("member_surname")} />
                    </FormField>
                    <FormField id={`if-m-${m.id}-ph`} label="Phone Number" required={idx === 0}>
                      <PhoneField
                        value={m.phone}
                        required={idx === 0}
                        onChange={(next) => updateMember(m.id, "phone", next)}
                      />
                    </FormField>
                    <FormField id={`if-m-${m.id}-em`} label="Email" required={idx === 0} error={idx === 0 ? fieldErrors.member_email : undefined} touched={idx === 0 ? touched.member_email : false}>
                      <input type="email" className="input w-full" value={m.email} onChange={(e) => updateMember(m.id, "email", e.target.value)} onBlur={() => idx === 0 && touch("member_email")} />
                    </FormField>
                  </FormFieldRow>
                </div>
              ))}
              {members.length < MAX_MEMBERS ? (
                <button type="button" className="text-sm font-semibold text-sky-600" onClick={() => setMembers((m) => [...m, { id: uid(), firstName: "", surname: "", phone: "", email: "" }])}>
                  + Add member
                </button>
              ) : null}
            </div>
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel buckets={IF_UPLOAD_BUCKETS} uploads={uploads} uploadingFiles={uploadingFiles} onUpload={handleUpload} onRemove={handleRemove} />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            documentsStepIndex={1}
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                rows: [
                  { label: "Scheme name", value: scheme.name },
                  { label: "Scheme number", value: scheme.number || "—" },
                  { label: "Credit History", value: credit },
                ],
                content: members.map((m, i) => (
                  <p key={m.id} className="text-sm">
                    <span className="text-slate-500">Member {i + 1}: </span>
                    {m.firstName} {m.surname} · {m.phone} · {m.email}
                  </p>
                )),
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
